import React from 'react';
import { socialProofImages } from '../data/images';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { trackCTAClick } from '../utils/analytics';

const SocialProof = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Schedule a Strategy Session', 'social_proof');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="social-proof" className="py-16 sm:py-24 bg-slate-950" data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Real Work. Real Impact.
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Trusted by Teams, Leaders,
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
              and Organizations
            </span>
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            From corporate boardrooms to community workshops, we deliver leadership development that creates lasting change.
          </p>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {socialProofImages.map((img, i) => (
            <div
              key={i}
              className={`group relative overflow-hidden rounded-xl ${i === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
              data-testid={`social-proof-image-${i}`}
            >
              <div className={`${i === 0 ? 'aspect-[4/3]' : 'aspect-square'}`}>
                <img
                  src={img.src}
                  alt={img.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-cyan-400">
                  {img.category}
                </span>
                <h3 className="text-white font-semibold text-xs sm:text-sm mt-0.5">{img.title}</h3>
                <p className="text-slate-400 text-[10px] sm:text-xs mt-0.5 hidden sm:block">{img.subtitle}</p>
                {img.outcome && (
                  <p className="text-emerald-400/80 text-[10px] sm:text-xs mt-1.5 hidden sm:block font-medium">{img.outcome}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 sm:mt-12">
          <Button
            onClick={scrollToConsultation}
            data-testid="social-proof-cta"
            size="lg"
            className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 py-5 text-base transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
          >
            Schedule a Strategy Session
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <button
            onClick={scrollToConsultation}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium group/micro"
          >
            Bring this to your team
            <ArrowRight className="h-3.5 w-3.5 group-hover/micro:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
